import type { ExperienceDraft } from '@/lib/experience-draft';
import { draftAsContent } from '@/lib/experience-draft';
import type {
  CriterionCard,
  ExperienceVariable,
  ExperienceVariableKind,
  InputFormat,
  InputLines,
  ProtocolContent,
  ScopeBreach,
  VariablePlace,
  VersionPin,
} from '@matter_extractor/shared';
import {
  assertNever,
  experienceSegmentsOf,
  experienceVariableToken,
  experienceVariablesOf,
  firstChoiceOf,
  pageChoiceName,
  variableScopeBreach,
} from '@matter_extractor/shared';

/**
 * La pastille de variable, ses encres et ce qu'elle sait (refonte modulaire
 * §8.6 ; grill Variables du 2026-09-16, D5 et D6 ; maquette
 * `docs/specs/maquettes/variables/`, planches 2 et 3 ; issue #88).
 *
 * §8.6 stores a variable as a TOKEN and displays it with its current name, so
 * a rename invalidates nothing. D5 draws that token the same way in the five
 * edit zones - the WYSIWYG of a Texte block, a Checkbox label, the persona
 * overlay, the Contexte of the protocol page, the directions of a protocol -
 * and this module is the ONE place that decides what the pill says and which
 * ink it wears. Written five times, the five would have disagreed on the
 * unknown case within a lot.
 *
 * ## Three things a pill knows, and where each comes from
 *
 * - its FACTS - the page and block of a field, the buttons of a choice, the
 *   step and obligation of an objective - read off the experience's draft and
 *   the protocol its protocol page plays (`variableFactsOf`);
 * - its REACH - whether it resolves WHERE it is cited (§8.6, D3), which is the
 *   pure rule of `shared` (`variableScopeBreach`) and nothing of this file's;
 * - its PAINT - the label and the ink - derived from the two above (`pillOf`).
 *
 * ## The five inks are a meaning, not a decoration (D5)
 *
 * An objective wears the bleu nuit of the protocol editor, a criterion its
 * green, a field and a choice share the bleu ciel - they are the same thing to
 * the interviewer, what the person said, and the fiche tells them apart. An
 * UNKNOWN variable stays grey and named by its kind (« @objectif inconnu »),
 * never blanked: §8.6 refuses it at the save, and a blank would hide the very
 * defect the refusal exists for. A variable OUT OF REACH resolves, but not
 * where it is cited, and wears yellow for it.
 *
 * ## Unread is not unknown
 *
 * A protocol or a criteria library still in flight has not said anything yet,
 * so a pill that depends on it is PENDING and keeps its kind's ink under a
 * neutral name (« @objectif »): the régime `protocol-editor.tsx` holds with its
 * `criteriaRead` flag - a library not read accuses nobody.
 */

/* ------------------------------------------------------------------- inks */

/**
 * The six inks of D5 - `value` is what a field and a choice share - and the
 * violet of `@noyau` since issue #151: « @noyau a sa propre encre » (maquette
 * noyau README), distinct from the objective's navy, the field's sky blue and
 * the six colours of the noyaux themselves, which a pill must never borrow.
 */
export type VariableInk =
  | 'objective'
  | 'criterion'
  | 'value'
  | 'noyau'
  | 'unknown'
  | 'out-of-scope';

/** The admin tokens of each ink (`styles.css`). The reader paints its own pill in the theme. */
export const PILL_INK_CLASS: Readonly<Record<VariableInk, string>> = {
  objective: 'bg-bleu-nuit-tint text-bleu-nuit',
  criterion: 'bg-vert-tint text-vert-deep',
  value: 'bg-bleu-ciel-tint text-bleu-ciel-deep',
  noyau: 'bg-violet-tint text-violet-deep',
  unknown: 'bg-gray-100 text-gray-600',
  'out-of-scope': 'bg-jaune-tint text-jaune-deep',
};

/** The one shape of the pill: insécable, on the baseline, the caret cannot split it. */
export const PILL_SHAPE_CLASS = 'inline whitespace-nowrap rounded-pill px-2 font-semibold';

/* -------------------------------------------------------------- the sources */

/** The protocol the protocol page plays, READ - what names an objective. */
export type VariableProtocol = {
  readonly id: string;
  readonly name: string;
  /** What the page cites the protocol at: `latest`, or the number it pins. */
  readonly pin: VersionPin;
  /** The number of the version whose content is read, or null when unknown. */
  readonly version: number | null;
  readonly content: ProtocolContent;
};

/**
 * Everything a pill may need to know, from the two sides an experience reads.
 *
 * `protocolRead` and `criteriaRead` are the two « has the read landed » flags:
 * false makes a protocol variable PENDING rather than unknown. `protocol` is
 * null both when nothing is chosen and while the read is in flight; the flag
 * tells the two apart.
 */
export type VariableSources = {
  readonly draft: ExperienceDraft;
  readonly protocol: VariableProtocol | null;
  readonly protocolRead: boolean;
  readonly criteria: readonly CriterionCard[];
  readonly criteriaRead: boolean;
};

/** What a screen knows BESIDES the draft - the reads, and where « Ouvrir » may land. */
export type VariableScope = Omit<VariableSources, 'draft'> & {
  /** The experience's id, or null before the first save: the fiche's links need it. */
  readonly experienceId: string | null;
};

/** The scope of a screen that has no experience to read: the protocol editor, a test. */
export const NO_VARIABLE_SCOPE: VariableScope = {
  protocol: null,
  protocolRead: true,
  criteria: [],
  criteriaRead: true,
  experienceId: null,
};

/* ---------------------------------------------------------------- the facts */

/** The protocol an objective belongs to, as the fiche names it. */
export type VariableProtocolFacts = {
  readonly id: string;
  readonly name: string;
  readonly pin: VersionPin;
  readonly version: number | null;
};

/** What is known of ONE variable, per kind - what the fiche says (D6). */
export type VariableFacts =
  | {
      readonly kind: 'field';
      readonly name: string;
      readonly pageId: string;
      readonly pageIndex: number;
      readonly pageName: string;
      readonly blockId: string;
      readonly blockType: 'input' | 'checkbox';
      readonly required: boolean;
      /** The block's own label - « Votre prénom ». */
      readonly label: string;
      /** An Input's two facets; null on a Checkbox, and the format null on a multi-line Input. */
      readonly lines: InputLines | null;
      readonly format: InputFormat | null;
    }
  | {
      readonly kind: 'choice';
      readonly name: string;
      readonly pageId: string;
      readonly pageIndex: number;
      readonly pageName: string;
      /** The labels of the buttons the person chooses between, in reading order. */
      readonly buttons: readonly string[];
    }
  | {
      readonly kind: 'objective';
      readonly name: string;
      readonly stepIndex: number;
      readonly stepName: string;
      readonly mandatory: boolean;
      readonly direction: string;
      /** The protocol that declares it, for the fiche's pill and its link; null inside that protocol's own editor. */
      readonly protocol: VariableProtocolFacts | null;
    }
  | {
      readonly kind: 'criterion';
      readonly name: string;
    }
  | {
      /** `@noyau` (noyau.md §3.3): given by the first choice, whose page is its provenance. */
      readonly kind: 'noyau';
      readonly name: string;
      readonly pageId: string;
      readonly pageIndex: number;
      readonly pageName: string;
    };

/**
 * A variable as ONE zone sees it: its facts, or none; whether the read that
 * would name it is still in flight; and why it is out of reach here, or null.
 */
export type VariableStanding = {
  readonly variable: ExperienceVariable;
  readonly facts: VariableFacts | null;
  readonly pending: boolean;
  readonly breach: ScopeBreach | null;
};

/** What a zone hands the pill module: one answer per token it meets. */
export type VariableDescriber = (variable: ExperienceVariable) => VariableStanding;

/** The facts of a field or a choice, read off the timeline. */
function timelineFacts(draft: ExperienceDraft, variable: ExperienceVariable): VariableFacts | null {
  if (variable.kind === 'field') {
    for (const [pageIndex, page] of draft.pages.entries()) {
      for (const temps of page.temps) {
        for (const block of temps.blocks) {
          if (block.id !== variable.id) continue;
          if (block.type !== 'input' && block.type !== 'checkbox') return null;
          return {
            kind: 'field',
            name: block.variableName,
            pageId: page.id,
            pageIndex,
            pageName: page.name,
            blockId: block.id,
            blockType: block.type,
            required: block.required,
            label: block.label,
            lines: block.type === 'input' ? block.lines : null,
            format: block.type === 'input' && block.lines === 'single' ? block.format : null,
          };
        }
      }
    }
    return null;
  }
  if (variable.kind === 'choice') {
    const pageIndex = draft.pages.findIndex((page) => page.id === variable.id);
    const page = draft.pages[pageIndex];
    if (page === undefined) return null;
    // `pageChoiceName` is the ONE predicate of « this page writes a choice »
    // (§8.6): a page with a single button, or with remembering buttons none of
    // which is named, writes none - and the token is then unresolved.
    const name = pageChoiceName(page);
    if (name === null) return null;
    return {
      kind: 'choice',
      name,
      pageId: page.id,
      pageIndex,
      pageName: page.name,
      buttons: page.temps
        .flatMap((temps) => temps.blocks)
        .filter((block) => block.type === 'button')
        .map((block) => block.label),
    };
  }
  return null;
}

/** What an objective's facts are read off: the steps of a content, or of the draft being edited. */
export type ObjectiveSteps = readonly {
  readonly name: string;
  readonly objectives: readonly {
    readonly id: string;
    readonly name: string;
    readonly mandatory: boolean;
    readonly direction: string;
  }[];
}[];

/** The facts of an objective, read off the protocol the protocol page plays. */
export function objectiveFactsOf(
  steps: ObjectiveSteps,
  id: string,
  protocol: VariableProtocolFacts | null,
): VariableFacts | null {
  for (const [stepIndex, step] of steps.entries()) {
    const objective = step.objectives.find((one) => one.id === id);
    if (objective === undefined) continue;
    return {
      kind: 'objective',
      name: objective.name,
      stepIndex,
      stepName: step.name,
      mandatory: objective.mandatory,
      direction: objective.direction,
      protocol,
    };
  }
  return null;
}

/**
 * What is known of a variable, and whether the answer is final.
 *
 * A field or a choice is answered by the draft alone, so it is never pending.
 * An objective waits for the protocol, a criterion for the library: until
 * their read lands, `pending` is true and `facts` null - which `pillOf` paints
 * in the kind's own ink, never in grey.
 */
export function variableFactsOf(
  sources: VariableSources,
  variable: ExperienceVariable,
): { readonly facts: VariableFacts | null; readonly pending: boolean } {
  switch (variable.kind) {
    case 'field':
    case 'choice':
      return { facts: timelineFacts(sources.draft, variable), pending: false };
    case 'objective': {
      if (!sources.protocolRead) return { facts: null, pending: true };
      const protocol = sources.protocol;
      if (protocol === null) return { facts: null, pending: false };
      return {
        facts: objectiveFactsOf(protocol.content.steps, variable.id, {
          id: protocol.id,
          name: protocol.name,
          pin: protocol.pin,
          version: protocol.version,
        }),
        pending: false,
      };
    }
    case 'criterion': {
      if (!sources.criteriaRead) return { facts: null, pending: true };
      const card = sources.criteria.find((one) => one.id === variable.id);
      return {
        facts: card === undefined ? null : { kind: 'criterion', name: card.name },
        pending: false,
      };
    }
    case 'noyau': {
      // Answered by the timeline alone: the FIRST choice gives the retained
      // noyau (noyau.md §3.3), and without one `@noyau` names nothing - which
      // the save refuses (`noyau-first-choice-missing`).
      const first = firstChoiceOf(draftAsContent(sources.draft));
      return {
        facts:
          first === null
            ? null
            : {
                kind: 'noyau',
                name: 'noyau',
                pageId: first.pageId,
                pageIndex: first.pageIndex,
                pageName: first.pageName,
              },
        pending: false,
      };
    }
    default:
      return { facts: null, pending: false };
  }
}

/**
 * A variable as the zone at `place` sees it - the facts plus the reach rule.
 *
 * `place` is null for a zone the reach rule does not judge: a protocol's own
 * direction, which has no page to be served on. A criterion, a pending or an
 * unknown variable never carries a breach either: the first resolves
 * everywhere, and the two others are refused for another reason.
 */
export function variableStandingOf(
  sources: VariableSources,
  place: VariablePlace | null,
  variable: ExperienceVariable,
): VariableStanding {
  const { facts, pending } = variableFactsOf(sources, variable);
  const breach =
    place === null || facts === null
      ? null
      : variableScopeBreach(draftAsContent(sources.draft), place, variable);
  return { variable, facts, pending, breach };
}

/** The describer of ONE zone, ready to hand to a painted field or a panel. */
export function variableDescriber(
  sources: VariableSources,
  place: VariablePlace | null,
): VariableDescriber {
  return (variable) => variableStandingOf(sources, place, variable);
}

/**
 * Where a zone stands, as its fiche reads « Rend ici » (D4): a place of an
 * experience, or - since issue #158 - the READING for the analyst of a
 * protocol, where an objective renders its NAME for the analyst and never its
 * matter (noyau.md §4.9), and - since issue #177 - the CONSIGNE of the rangeur,
 * which renders the same name to the rangeur (scenario-succes.md §4). A
 * protocol's own direction is the `null` of `VariableZone.place`, as it always was.
 */
export type ZonePlace =
  | VariablePlace
  | { readonly kind: 'reading' }
  | { readonly kind: 'rangement' };

/** The place of the reading for the analyst - one value, since a protocol has one reading. */
export const READING_PLACE: ZonePlace = { kind: 'reading' };

/** The place of the consigne of the rangeur - one value, since a protocol has one rangement (#177). */
export const RANGEMENT_PLACE: ZonePlace = { kind: 'rangement' };

/**
 * ONE zone, as a field or a panel receives it: what each token is HERE, where
 * here is, and which experience « Ouvrir » lands in.
 *
 * One value and not three props, because it crosses three components on its
 * way from the page editor's body to the WYSIWYG, and a trio threaded through
 * three signatures is a trio that arrives incomplete somewhere.
 */
export type VariableZone = {
  readonly describe: VariableDescriber;
  /**
   * Null for a zone the reach rule does not judge - a protocol's own direction;
   * `READING_PLACE` for the protocol's reading for the analyst (#158), which
   * the reach rule does not judge either.
   */
  readonly place: ZonePlace | null;
  readonly experienceId: string | null;
};

export function variableZoneOf(
  sources: VariableSources,
  place: VariablePlace | null,
  experienceId: string | null,
): VariableZone {
  return { describe: variableDescriber(sources, place), place, experienceId };
}

/**
 * What a PREVIEW names each token (D4, D5): the pill's label - « @prénom »,
 * or « @objectif inconnu » - whatever the place, because a preview paints the
 * name and never a value, and the reach is the edit zone's business.
 */
export function previewVariableNames(
  sources: VariableSources,
): (variable: ExperienceVariable) => string {
  return (variable) => pillOf(variableStandingOf(sources, null, variable)).label;
}

/** The current NAME of a token, or null - what the save box names a refused line by. */
export function variableNamer(
  sources: VariableSources,
): (variable: ExperienceVariable) => string | null {
  return (variable) => variableNameOf(variableStandingOf(sources, null, variable));
}

/* ---------------------------------------------------------------- the paint */

/** « @objectif inconnu » - an unknown variable is named by its kind, never blanked (D5). */
export const UNKNOWN_LABEL: Readonly<Record<ExperienceVariableKind, string>> = {
  field: '@champ inconnu',
  choice: '@choix inconnu',
  objective: '@objectif inconnu',
  criterion: '@critère inconnu',
  // `@noyau` is one variable per experience, never an unknown one: its name is
  // its whole identity (noyau.md §3.3), and without a first choice it simply
  // resolves to nothing - which the refusal of the fiche says.
  noyau: '@noyau',
};

/** « @objectif » - a variable whose read has not landed keeps a neutral name. */
export const PENDING_LABEL: Readonly<Record<ExperienceVariableKind, string>> = {
  field: '@champ',
  choice: '@choix',
  objective: '@objectif',
  criterion: '@critère',
  noyau: '@noyau',
};

const KIND_INK: Readonly<Record<ExperienceVariableKind, VariableInk>> = {
  field: 'value',
  choice: 'value',
  objective: 'objective',
  criterion: 'criterion',
  noyau: 'noyau',
};

/** What the pill DRAWS: its label, its ink, and the token it stands for. */
export type PillPaint = {
  readonly label: string;
  readonly ink: VariableInk;
  /** `objectif:<id>` - the token without its braces, as `data-variable` carries it. */
  readonly token: string;
};

/** The token without its braces: `experienceVariableToken` stays the only spelling of the prefix. */
export function pillToken(variable: ExperienceVariable): string {
  return experienceVariableToken(variable).slice(2, -2);
}

/** The variable a `data-variable` names, or null on a mark nothing wrote. */
export function variableOfPillToken(token: string): ExperienceVariable | null {
  return experienceVariablesOf(`{{${token}}}`)[0] ?? null;
}

/**
 * The current NAME of a variable, or null when nothing resolves it - what the
 * save box and the preview spend, without the pill around it.
 */
export function variableNameOf(standing: VariableStanding): string | null {
  const name = standing.facts?.name ?? null;
  return name === null || name === '' ? null : name;
}

export function pillOf(standing: VariableStanding): PillPaint {
  const token = pillToken(standing.variable);
  const kind = standing.variable.kind;
  if (standing.pending) return { label: PENDING_LABEL[kind], ink: KIND_INK[kind], token };
  const name = variableNameOf(standing);
  if (name === null) return { label: UNKNOWN_LABEL[kind], ink: 'unknown', token };
  return {
    label: `@${name}`,
    ink: standing.breach === null ? KIND_INK[kind] : 'out-of-scope',
    token,
  };
}

/* ----------------------------------------------------- the painted pieces */

/** One piece of a plain text as a painted field draws it: a run of text, or a pill. */
export type PaintedPiece =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'pill'; readonly standing: VariableStanding; readonly paint: PillPaint };

/** A plain text - a direction, a label, the Contexte - split into what to paint. */
export function paintedPieces(text: string, describe: VariableDescriber): PaintedPiece[] {
  return experienceSegmentsOf(text).map((segment) => {
    if (segment.kind === 'text') return { kind: 'text', text: segment.text };
    const standing = describe(segment.variable);
    return { kind: 'pill', standing, paint: pillOf(standing) };
  });
}

/**
 * What the DOM currently shows, as a string two paints can be compared on.
 *
 * A painted field repaints only when this changes: the labels and inks are in
 * it, so a rename elsewhere repaints, and a describer rebuilt on every render
 * with the same answers does not - which is what keeps the caret where it is.
 */
export function paintSignature(pieces: readonly PaintedPiece[]): string {
  return JSON.stringify(
    pieces.map((piece) =>
      piece.kind === 'text' ? piece.text : [piece.paint.token, piece.paint.label, piece.paint.ink],
    ),
  );
}

/** The block elements a browser drops into a contentEditable on Enter. */
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE']);

/**
 * A painted surface read back as the text a version stores.
 *
 * Text nodes as they are, a pill as its token, a `<br>` and a nested block as
 * a newline - every shape a browser produces inside a contentEditable when
 * somebody presses Enter or pastes. `innerText` would have said the same thing
 * in a browser and NOTHING in jsdom, where it is not implemented: the one rule
 * of a painted field a unit test can hold would have been the one it cannot
 * run. Moved here from `protocol-step-draft.ts` when the five zones met on one
 * pill (D5).
 */
export function serializePainted(root: Node): string {
  let out = '';
  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.textContent ?? '';
        continue;
      }
      if (child instanceof HTMLElement) {
        const variable = child.dataset.variable;
        if (variable !== undefined) {
          out += `{{${variable}}}`;
          continue;
        }
        if (child.tagName === 'BR') {
          out += '\n';
          continue;
        }
        if (BLOCK_TAGS.has(child.tagName) && out !== '' && !out.endsWith('\n')) out += '\n';
      }
      walk(child);
    }
  };
  walk(root);
  return out;
}

/** A selection counted in STORED characters - where it sits in the text a version stores. */
export type StoredSelection = { readonly start: number; readonly end: number };

/**
 * What a painted field reports when its bound refuses an input (#158): where
 * the input came from - « collé », « déposé », or anything else -, the stored
 * characters it would have added, and the room left under the bound, the
 * selection it would have replaced counted free.
 */
export type BoundRefusal = {
  readonly by: 'paste' | 'drop' | 'input';
  readonly added: number;
  readonly room: number;
};

/**
 * What a bounded field says when it refuses an input (#158, #160): that
 * NOTHING was added - a paste is never cut to fit -, what it would have taken,
 * and the room left, `subject` naming what stands at its bound when there is
 * none. A count beside a field is text nobody hears change, so this sentence
 * is where a refusal is said at all.
 */
export function boundRefusalSentence(refusal: BoundRefusal, max: number, subject: string): string {
  const added = `${refusal.added} caractère${refusal.added === 1 ? '' : 's'}`;
  const room =
    refusal.room === 0
      ? `${subject} est à sa borne de ${max} caractères`
      : `il n’en reste que ${refusal.room} de libre${refusal.room === 1 ? '' : 's'} sur ${max}`;
  switch (refusal.by) {
    case 'paste':
      return `Rien n’a été collé : ce texte fait ${added}, et ${room}.`;
    case 'drop':
      return `Rien n’a été déposé : ce texte fait ${added}, et ${room}.`;
    case 'input':
      return `Rien n’a été ajouté : il faudrait ${added}, et ${room}.`;
    default:
      return assertNever(refusal.by, 'boundRefusalSentence');
  }
}

/** A stored text once a token has landed in it, and where the caret goes (#160). */
export type TokenLanding = { readonly text: string; readonly caret: number };

/**
 * The `@query` the palette was opened on, replaced by the token to the
 * character, its `@` included and nothing added around it: what was typed
 * before the `@` is what the operator meant the pill to follow.
 */
export function replaceAtQuery(text: string, query: StoredSelection, token: string): TokenLanding {
  return {
    text: `${text.slice(0, query.start)}${token}${text.slice(query.end)}`,
    caret: query.start + token.length,
  };
}

/** After these, a pill needs no space: a space or a line, an opening bracket, quote or apostrophe. */
const OPENING = /[\s([{«“‘’'"]/u;
/** Before these, it needs one: a word, another variable, an opening bracket or quote. */
const WORDLIKE = /[\p{L}\p{N}@{([«“]/u;

/**
 * A token put at a caret, in place of the selection it spans - the `{ }` of a
 * painted field since #160, which always wrote at the end before.
 *
 * A space parts the pill from what it would otherwise stick to - a word or a
 * punctuation before it, a word after it -, never doubled against a space or a
 * line, never pushed off a closing punctuation. At the end of the text it keeps
 * a space after it, as that end-only `{ }` did, so the operator writes on. The
 * caret goes where what it wrote ends.
 */
export function insertAtCaret(text: string, caret: StoredSelection, token: string): TokenLanding {
  const head = text.slice(0, caret.start);
  const tail = text.slice(caret.end);
  const previous = head.at(-1);
  const next = tail.at(0);
  const lead = previous === undefined || OPENING.test(previous) ? '' : ' ';
  const trail = next === undefined || WORDLIKE.test(next) ? ' ' : '';
  const written = `${lead}${token}${trail}`;
  return { text: `${head}${written}${tail}`, caret: caret.start + written.length };
}

/**
 * The live selection inside `root`, in stored characters, or null when it is
 * not there (#158).
 *
 * Each end is the length of what precedes it, read back by `serializePainted`
 * - so a pill counts as its token, and a `<div>` or a `<br>` a browser made on
 * Entrée as the `\n` the field will emit: the two numbers can never disagree
 * with the text. It is the painted field's `plainRangeOf`
 * (`lib/rich-text-editor.ts`), measured with the painted field's own rules.
 */
export function storedSelectionOf(root: HTMLElement): StoredSelection | null {
  const selection = window.getSelection();
  if (selection === null || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
  const upTo = (node: Node, offset: number): number => {
    const prefix = document.createRange();
    prefix.setStart(root, 0);
    prefix.setEnd(node, offset);
    return serializePainted(prefix.cloneContents()).length;
  };
  return {
    start: upTo(range.startContainer, range.startOffset),
    end: upTo(range.endContainer, range.endOffset),
  };
}

/**
 * Puts the selection at `selection` in a root FRESHLY painted from its stored
 * text - text nodes and pills only, as `paintedPieces` lays them out - which
 * is the one shape a repaint leaves (#158). An offset inside a pill's token
 * lands after it, a token having no middle; one past the text, at its end.
 */
export function selectStored(root: HTMLElement, selection: StoredSelection): void {
  const pointAt = (offset: number): { node: Node; offset: number } => {
    let seen = 0;
    const children = Array.from(root.childNodes);
    for (const [index, child] of children.entries()) {
      if (child.nodeType === Node.TEXT_NODE) {
        const length = (child.textContent ?? '').length;
        if (offset <= seen + length) return { node: child, offset: offset - seen };
        seen += length;
        continue;
      }
      if (offset <= seen) return { node: root, offset: index };
      seen += serializePainted(fragmentOf(child)).length;
      if (offset <= seen) return { node: root, offset: index + 1 };
    }
    return { node: root, offset: children.length };
  };
  const start = pointAt(selection.start);
  const end = pointAt(selection.end);
  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  const live = window.getSelection();
  live?.removeAllRanges();
  live?.addRange(range);
}

/** `node` alone in a fragment, for `serializePainted` to weigh it - a clone, the DOM untouched. */
function fragmentOf(node: Node): DocumentFragment {
  const fragment = document.createDocumentFragment();
  fragment.append(node.cloneNode(true));
  return fragment;
}
